// Server-authoritative zone read/write path (hidden-info domain R2c).
//
// Drawing + every pile/hand transition flows through authoritative commands so
// the server owns zone truth and reveals a moved card ONLY to the entitled seat.
// The actor mutates LOCALLY first (drawCard / sendCardToPile already moved the
// card + drew the mesh), THEN emits DRAW / MOVE_TO_ZONE; the authoritative diff
// confirms it and propagates the COUNT delta to the opponent. Replacing the
// legacy pile:sync / spell:todraw P2P broadcasts for hidden draw piles.
//
// The opponent never receives the drawn/moved card identity (the server's
// per-seat projection strips it); their view updates only via the count deltas
// in the projected diff. This mapper folds those counts onto the opponent's
// placeholder pile stacks + the opponent hand fan. The LOCAL seat's piles/hand
// are single-writer (only this client mutates them), so the mapper never touches
// them. Folding the local seat would fight the already-applied local mutation.

import { DRAW, MOVE_TO_ZONE, SHUFFLE, PILE_REORDER, SET_PILE_REVEALED, SET_POSITION } from '../../../utils/game/net/matchCore';
import { createPlaceholderCard, createCardInstance, createPile } from '../../../utils/game/gameState';
import { applyRemotePoseAndRest, syncMeshFromBody } from '../../../utils/game/physicsWorld';
import { computeGridPositions } from '../../../utils/game/grid';
import { getGameGrid, getSpawnPoint } from '../../../utils/game/spawnConfig';
import { resolveAuraAnchor, sampleCellGeometry } from '../../../utils/game/engine/core';
import { layoutCellRest } from '../../../utils/game/cellRestLayout';
import { fanPosesForFocusedCell, glideMeshXZ } from './cellFocus';
import { resolvePrinting } from '../../../utils/sorcery/foil';
import { k, t } from '../../../localization/t';

// Which printing a mirror card renders as. The registry carries printingId /
// foiling from the deal; falling back to printings[0] silently downgrades an
// alt-art / foil card to the standard non-foil art (Standard sorts first for
// 1092 of the 1095 dual-variant cards), so every rebuild-from-mirror site must
// resolve through this instead.
function mirrorPrinting(cat, mc) {
  return resolvePrinting(cat, mc?.printingId, mc?.foiling ?? 'S') || cat.printings?.[0];
}

// Materialize a mirror card into a local instance. Every fold that renders a
// server-known card goes through here so tokenhood can't be dropped by one of
// them: createCardInstance can't infer it (a Skeleton row looks like any other
// Minion in the catalog), and a token that loses the flag renders FULL SIZE
// (cardMesh gates TOKEN_CARD_SCALE on isToken) and slips past the "tokens
// always banish" redirect in cardPileLifecycle.
function instanceFromMirror(cat, mc, rotated, id) {
  const card = createCardInstance(cat, mirrorPrinting(cat, mc), rotated, id);
  if (mc?.isToken) card.isToken = true;
  return card;
}

export function pileRevealedCommand(seat, pileName, revealed) {
  return { type: SET_PILE_REVEALED, payload: { seat, pileName, revealed } };
}

export function shuffleCommand(seat, pileName, order) {
  return { type: SHUFFLE, payload: { seat, pileName, order } };
}

// For the DRAW piles, the two halves of the client number a pile from OPPOSITE
// ends:
//   mirror / server truth : index 0 = TOP of deck (the reducer's DRAW shifts 0,
//                           and MOVE_TO_ZONE appends, i.e. to the bottom)
//   pile.cards / renderer : LAST element = TOP of deck (gameState.drawFromPile
//                           takes cards[len-1]; cardMesh renders the last as the
//                           visible top face)
// So every crossing of that boundary must reverse. Missing the inversion is not
// cosmetic: the client, not the server, chooses which card a draw takes (see
// cardPileLifecycle.drawCard -> drawFromPile -> MOVE_TO_ZONE), so a card the
// server buried at the bottom would be handed back on the very next draw. The
// mulligan-comes-straight-back report (GH-76).
//
// Only these three zones are numbered top-first, because only they are drawn
// from (drawCard emits its own DRAW / MOVE_TO_ZONE for exactly this set). The
// cemetery and banished piles are ARRIVAL-ordered on both sides. A card is
// appended as it lands, so mirror-last and render-last both mean "most recently
// arrived" and inverting them would show the oldest card as the pile's face.
const TOP_INDEXED_ZONES = new Set(['spellbook', 'atlas', 'collection']);

export function isTopIndexedZone(zone) {
  return TOP_INDEXED_ZONES.has(zone);
}

function renderOrderFromMirror(ids) {
  return [...ids].reverse();
}

// Render order (bottom-to-top) -> the server's top-to-bottom id list. Shared by
// both commands that assert a pile order on the wire: SHUFFLE (whose order the
// server then discards) and PILE_REORDER (whose order it honours).
export function shuffleOrderFromRender(cards) {
  return [...(cards || [])].reverse().map((c) => c.id);
}

// A player's own arrangement of an ordered draw pile. The scry outcome. The
// counterpart to shuffleCommand: same shape, opposite server treatment. SHUFFLE
// exists so the server can refuse to trust a client order; PILE_REORDER exists
// because for a scry the client order IS the mechanic, and the server bounds it
// to a permutation of the pile it already holds instead of replacing it.
export function pileReorderCommand(seat, zone, order) {
  return { type: PILE_REORDER, payload: { seat, zone, order } };
}

export function drawCommand(seat, pileName, count = 1) {
  return { type: DRAW, payload: { seat, pileName, count } };
}

export function moveToZoneCommand(cardId, toZone, { index, faceDown, autoPay, manualDraw } = {}) {
  const payload = { cardId, toZone };
  if (index !== undefined) payload.index = index;
  if (faceDown !== undefined) payload.faceDown = faceDown;
  // W3: a Magic cast (hand → storyline) opts out of the server-computed mana
  // debit with autoPay:false (manual mana / consumed free-cast). The reducer
  // ignores the field; the server's storyline-debit fold reads it.
  if (autoPay === false) payload.autoPay = false;
  // A deck→hand drag-draw. The reducer discharges the start-phase draw
  // obligation only for a marked move (valkenhall-qa#158); an ability's
  // deck→hand move stays unmarked.
  if (manualDraw) payload.manualDraw = true;
  return { type: MOVE_TO_ZONE, payload };
}

// Positions domain (D5): assert an in-play card's resting board location. The
// logical cell {col,row}. World coords are the aura exception (intersection
// anchors); omit them for every cell-bound card and the reducer derives the
// resting pose from cell + stackSeq.
export function positionCommand(cardId, col, row, x, z) {
  const payload = { cardId, col, row };
  if (typeof x === 'number' && typeof z === 'number') {
    payload.x = x;
    payload.z = z;
  }
  return { type: SET_POSITION, payload };
}

// The authoritative seat the LOCAL player owns (host → p1, guest → p2) and the
// opponent's seat / pile orientation (the opponent spawns rotated when we are
// the host).
function seatLayout(host) {
  const localIsHost = !!host.state?.isHost;
  return {
    localSeat: localIsHost ? 'p1' : 'p2',
    oppSeat: localIsHost ? 'p2' : 'p1',
    oppRotated: localIsHost, // opponent is p2 (rotated) when we are host
  };
}

// Resize a placeholder-stack pile to `target`, mutating in place so existing
// mesh.userData.pile references stay valid. Returns true when the length changed.
function resizePlaceholderPile(pile, target, rotated, isSite) {
  if (!pile || !Array.isArray(pile.cards)) return false;
  const cur = pile.cards.length;
  if (cur === target) return false;
  if (target < cur) {
    pile.cards.length = target;
  } else {
    for (let i = cur; i < target; i++) pile.cards.push(createPlaceholderCard(rotated, isSite));
  }
  return true;
}

// Fold the authoritative OPPONENT pile + hand COUNTS from the mirror onto the
// opponent's placeholder stacks + hand fan. Idempotent: a diff whose counts
// already match (the actor's own resource/turn diff) is a no-op. Skips the local
// seat entirely (single-writer). The mirror has already stripped identities, so
// only counts are ever read here.
export function applyZonesFromMirror(host, mirrorState) {
  // Solo: the pilot owns BOTH seats. There is no hidden opponent, no
  // placeholder stacks and no opponent hand fan. Both seats' real piles/hands
  // fold via applyOwnPilesFromMirror / applyHandFromMirror instead.
  if (host.props?.isCasualSandbox) return;
  const pileCounts = mirrorState?.pileCounts;
  const handCounts = mirrorState?.handCounts;
  if (!pileCounts && !handCounts) return;

  // A replay viewer is SEATED (the chosen viewpoint), not seatless: its
  // omniscient state carries the viewed seat's real piles/hand, which fold
  // via applyOwnPilesFromMirror / applyHandFromMirror. Rendering count
  // placeholders for that seat too would draw every pile twice, so only the
  // opposite seat renders count-based here (its hand fan still builds from
  // the registry below, face-up since every identity is present).
  const spectating = !!host.props?.isSpectating && !host.props?.replayDriver;
  const { oppSeat, oppRotated } = seatLayout(host);
  // A live spectator has no own seat: BOTH seats render count-based (the
  // spectator projection carries pileCounts/handCounts for both). The top
  // fan (rotated seat, p2) doubles as the "opponent hand"; p1's hand stays
  // unrendered, matching what a player sees of a hand it can't inspect.
  const countSeats = spectating
    ? [{ seat: 'p1', rotated: false }, { seat: 'p2', rotated: true }]
    : [{ seat: oppSeat, rotated: oppRotated }];
  const fanSeat = spectating ? 'p2' : oppSeat;
  const fanRotated = spectating ? true : oppRotated;

  const gs = host.state?.gameState;
  let pilesChanged = false;

  // Replay: both seats' piles are REAL (folded by applyOwnPilesFromMirror),
  // so no count placeholders for anyone. The opponent hand fan below still
  // builds from the registry (face-up, inspectable).
  const pileSeats = host.props?.replayDriver ? [] : countSeats;
  for (const { seat, rotated } of pileSeats) {
    const seatPiles = pileCounts?.[seat];
    if (!seatPiles || !gs?.piles) continue;
    for (const [pileName, key, isSite] of [['Spellbook', 'spellbook', false], ['Atlas', 'atlas', true]]) {
      const target = seatPiles[key];
      if (typeof target !== 'number') continue;
      let pile = gs.piles.find((p) => p.name === pileName && !!p.rotated === rotated);
      if (!pile) {
        pile = createDrawPileFromMirror(host, key, rotated);
        if (pile) pilesChanged = true;
      }
      if (resizePlaceholderPile(pile, target, rotated, isSite)) pilesChanged = true;
    }
  }
  if (pilesChanged) host.updatePileMeshes?.();

  const oppHandCount = handCounts?.[fanSeat];
  if (typeof oppHandCount === 'number') {
    // Build the opponent hand fan from the card registry: a hidden card
    // arrives as a skeleton (which still carries isSite → landscape site
    // back), and a card revealed to this seat keeps full identity, so shown
    // cards render face-up without the retired hand:info relay. Owner-locked
    // unit-hand spells live in the hand ZONE server-side but render in the
    // unit-hand component. Exclude them from the fan. Order is unknown (the
    // projection withholds the opponent's hand array), so slots sort by
    // instance id for stability.
    const cards = mirrorState?.cards ?? {};
    const unitHandIds = new Set();
    for (const entry of Object.values(mirrorState?.unitHands ?? {})) {
      for (const s of entry?.cards ?? []) if (s?.id) unitHandIds.add(s.id);
    }
    const byId = host._sorceryCardIndex
      || (host._sorceryCardIndex = new Map((host.props.sorceryCards || []).map((c) => [c.unique_id, c])));
    const registrySlots = Object.entries(cards)
      .filter(([id, mc]) => mc?.owner === fanSeat && mc?.zone === 'hand' && !unitHandIds.has(id))
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([id, mc]) => {
        const cat = mc.cardId != null ? byId.get(mc.cardId) : null;
        if (!cat) return createPlaceholderCard(fanRotated, !!mc.isSite);
        const printing = mirrorPrinting(cat, mc);
        return {
          id,
          isSite: !!mc.isSite,
          revealed: true,
          cardId: mc.cardId,
          name: cat.name,
          imageUrl: printing?.image_url || '',
          foiling: printing?.foiling ?? mc.foiling ?? null,
        };
      });
    // Registry not landed yet (counts-only diff on a cold mirror): fall back
    // to plain count-driven placeholders so the fan size is still right.
    const slots = registrySlots.length
      ? registrySlots
      : Array.from({ length: oppHandCount }, () => createPlaceholderCard(fanRotated, false));
    const slotSig = (c) => (c.revealed ? `r:${c.cardId}` : c.isSite ? 's' : 'c');
    const sig = slots.map(slotSig).join('|');
    // Seed the comparison signature from the currently-rendered fan so a
    // cold fold over an already-correct render is a no-op (no mesh churn).
    if (host._oppHandSig === undefined) {
      host._oppHandSig = (host.state?.opponentHand ?? []).map(slotSig).join('|');
    }
    if (host._oppHandSig !== sig) {
      host._oppHandSig = sig;
      host.setState({ opponentHand: slots }, () => host.updateOpponentHandMeshes?.());
      // A shown card the inspector is displaying may have just been hidden
      // or played. Its snapshot is no longer authoritative; close it.
      const inspected = host.state.inspectedCard;
      if (inspected?._fromOpponentHand) {
        const stillRevealed = slots.some((c) => c.revealed && c.cardId === inspected.cardId);
        if (!stillRevealed) {
          host.setState({ inspectedCard: null, inspectorStack: [] });
          host.scene?.setPaused(false);
        }
      }
    }
  }
}

// Owner-locked spells ("Only It Can Cast." Omphalos / Morgana) live in the
// seat's HAND ZONE server-side but render ONLY in the unit-hand component,
// never the player hand fan. Both hand folds must skip them or a unit's draw
// leaks into the player's own hand. The mirror's unitHands is authoritative;
// the local gameState copy covers the actor's emit race (the unit-hand update
// lands one diff after the zone move).
export function isUnitHandSpell(id, mirrorState, gs) {
  const inMap = (map) => map && Object.values(map).some(
    (entry) => (entry?.spells || []).some((s) => s?.id === id),
  );
  if (mirrorState?.unitHands != null && mirrorState?.cards?.[id] && !mirrorState.cards[id].castLockOwnerId && !inMap(mirrorState.unitHands)) return false;
  return inMap(mirrorState?.unitHands) || inMap(gs?.unitHands);
}

// Unit-hand spells arrive from the mirror as FROZEN Immer objects carrying
// only { id, cardId, lock fields }. No name, no art. Rendering one outside
// the badge showed a card back, and the drag path threw on `spell.x =`
// (frozen object). Hydrate every entitled spell into a fresh MUTABLE
// instance enriched from the catalog; a back (no cardId) is just cloned.
// Idempotent: an already-hydrated spell object is reused as-is so React
// diffing and in-flight drags keep their references.
export function hydrateUnitHandSpells(host, unitHands) {
  if (!unitHands) return unitHands;
  const out = {};
  for (const [ownerId, entry] of Object.entries(unitHands)) {
    out[ownerId] = {
      ...entry,
      spells: (entry?.spells || []).map((sp) => {
        if (!sp) return sp;
        if (!Object.isFrozen(sp) && sp.name && sp.imageUrl) return sp;
        const full = sp.cardId ? host._getFullCard?.(sp.cardId) : null;
        return {
          ...sp,
          ...(sp._seenBy ? { _seenBy: [...sp._seenBy] } : {}),
          ...(full ? {
            name: sp.name ?? full.name,
            type: sp.type ?? full.type,
            imageUrl: sp.imageUrl ?? mirrorPrinting(full, sp)?.image_url ?? null,
          } : {}),
        };
      }),
    };
  }
  return out;
}

// RC2. Reconnect-authoritative hand restore. On a reconnect/resume the legacy
// snapshot blob can rebuild THIS seat's own hand as stripped, face-down, or
// art-less cards (the own-hand-face-down / missing-image bug). The server holds
// the authoritative hand (R2c: hands[seat] + the cards registry), and the
// per-seat projection keeps OUR own hand in full. So rebuild handCards from the
// mirror, resolving each cardId to the catalog for name/art. Fires on a `full`
// (the reconnect/resume signal); a no-op in live play because the hand already
// carries full identities and matches the mirror's id order.
export function applyHandFromMirror(host, mirrorState) {
  for (const { seat, handKey, rotated } of ownSeatViews(host)) {
    applyHandFromMirrorForSeat(host, mirrorState, seat, handKey, rotated);
  }
}

// The seats whose private containers are OURS to fold. Online: the one local
// seat (handCards, own rotation). Solo: BOTH seats. The merged solo
// projection carries both hands/pile orders, and the pilot's P2 hand lives on
// the suffixed handCards2 key (the existing sandbox dual-hand rendering).
export function ownSeatViews(host) {
  if (host.props?.isCasualSandbox) {
    return [
      { seat: 'p1', handKey: 'handCards', rotated: false },
      { seat: 'p2', handKey: 'handCards2', rotated: true },
    ];
  }
  const localSeat = host.state?.isHost ? 'p1' : 'p2';
  return [{ seat: localSeat, handKey: 'handCards', rotated: !host.state?.isHost }];
}

function applyHandFromMirrorForSeat(host, mirrorState, seat, handKey, ownRotated) {
  const mirrorHand = mirrorState?.hands?.[seat];
  const cards = mirrorState?.cards;
  if (!Array.isArray(mirrorHand) || !cards) return;

  // INVARIANT: a card is in exactly one place. If a card has a board mesh it is
  // ON THE BOARD, not in the hand. Even if matchState.hands still lists it
  // (a play that hasn't yet been recorded as a hand→board move leaves the hand
  // stale). Excluding board cards here stops the played-card-also-in-hand
  // duplication (a played site reappearing as a hand copy on reconnect/heal).
  // Owner-locked unit-hand spells share the hand zone but render elsewhere.
  const gs = host.state?.gameState;
  const hand = mirrorHand.filter((id) => !host.meshes?.has?.(id) && !isUnitHandSpell(id, mirrorState, gs));

  const current = host.state?.[handKey] || [];
  const curIds = current.map((c) => c.id);
  const sameOrder = curIds.length === hand.length && hand.every((id, i) => curIds[i] === id);
  const allIdentified = current.every((c) => c.cardId && (c.imageUrl || c.imagePath));
  if (sameOrder && allIdentified) return; // live hand is intact. Nothing to heal

  const byId = host._sorceryCardIndex
    || (host._sorceryCardIndex = new Map((host.props.sorceryCards || []).map((c) => [c.unique_id, c])));
  const rebuilt = hand
    .map((id) => {
      const mc = cards[id];
      if (!mc || mc.cardId == null) return null;
      const cat = byId.get(mc.cardId);
      return cat ? instanceFromMirror(cat, mc, ownRotated, id) : null;
    })
    .filter(Boolean);
  // Don't wipe a populated hand on a catalog miss. Only commit a faithful rebuild.
  if (hand.length > 0 && rebuilt.length !== hand.length) return;
  host.setState({ [handKey]: rebuilt });
}

// LIVE own-hand ADDITIONS (pure-view): a card the server put in OUR hand that
// the local hand doesn't render yet (a server-autonomous draw. A delegated
// `draw` op, Howl from Beyond's keepers) materializes here on the diff, not at
// the next reload. Additions-only: local removals (a cast in flight, an
// optimistic discard) are never fought. A card rendered ANYWHERE locally
// (board mesh, any pile) is skipped, so this can only fill a genuine render
// gap the server explicitly asserts.
// Per-card delay between staggered server-autonomous draws landing in the hand,
// so a multi-card draw (Queen of Midland, Seelie Court) reads as N distinct
// cards + N draw sounds rather than one instant pop.
export const DRAW_STAGGER_MS = 180;

export function applyHandAdditionsFromMirror(host, mirrorState) {
  for (const { seat, handKey, rotated } of ownSeatViews(host)) {
    applyHandAdditionsForSeat(host, mirrorState, seat, handKey, rotated);
  }
}

function applyHandAdditionsForSeat(host, mirrorState, seat, handKey, ownRotated) {
  const mirrorHand = mirrorState?.hands?.[seat];
  const cards = mirrorState?.cards;
  if (!Array.isArray(mirrorHand) || mirrorHand.length === 0 || !cards) return;
  const gs = host.state?.gameState;
  const current = host.state?.[handKey] || [];
  const haveIds = new Set(current.map((c) => c.id));
  const byId = host._sorceryCardIndex
    || (host._sorceryCardIndex = new Map((host.props.sorceryCards || []).map((c) => [c.unique_id, c])));
  const additions = [];
  let staleDropped = false;
  for (const id of mirrorHand) {
    if (haveIds.has(id)) continue;
    if (host._drawAnimIds?.has?.(id)) continue; // draw animation in flight. Lands on its own beat
    if (host._howlHoldIds?.has(id)) continue; // reveal in flight. BoardFx releases it on its resolve beat
    if (host.meshes?.has?.(id)) continue; // on the board. Optimistic play in flight
    // Server says HAND; a stale deck/pile render must not silence the addition.
    // The old skip left the card rendered in the deck AND absent from the hand
    // with nothing to re-trigger the fold (the hands[] diff never re-fires).
    // the "draw shows nothing until a hand refresh" bug. Drop the stale pile
    // render (the reconciler's leave rule) and fold the card in.
    for (const p of gs?.piles || []) {
      if (!Array.isArray(p.cards)) continue;
      const idx = p.cards.findIndex((c) => c.id === id);
      if (idx !== -1) {
        p.cards.splice(idx, 1);
        staleDropped = true;
        console.warn('[handAdditions] dropped stale pile render for server-drawn card', { id, pile: p.name });
      }
    }
    if (isUnitHandSpell(id, mirrorState, gs)) continue; // owner-locked. Renders in the unit hand, never the fan
    // Solo: the other piloted hand renders this id already. Not an addition,
    // unless the server MOVED it between the two hands (Pith Imp's steal and
    // its return): then it leaves the other fan and lands in this one.
    if (host.props?.isCasualSandbox) {
      const otherKey = handKey === 'handCards' ? 'handCards2' : 'handCards';
      const otherSeat = seat === 'p1' ? 'p2' : 'p1';
      if ((host.state?.[otherKey] || []).some((c) => c.id === id)) {
        if ((mirrorState.hands?.[otherSeat] || []).includes(id)) continue;
        host.setState({ [otherKey]: host.state[otherKey].filter((c) => c.id !== id) });
      }
    }
    const mc = cards[id];
    if (!mc || mc.cardId == null) continue;
    const cat = byId.get(mc.cardId);
    if (!cat) continue; // catalog miss. Never render a blank
    additions.push(instanceFromMirror(cat, mc, ownRotated, id));
  }
  if (staleDropped) {
    host.updatePileMeshes?.();
    host.forceUpdate?.();
  }
  if (additions.length === 0) return;
  scheduleHandAdditions(host, additions, { handKey, seat, current });
}

// Route server-autonomous hand additions through the animated draw path,
// staggered so the count is readable. Falls back to an instant state add in a
// headless/host-stub context (no `_animateDrawnCardToHand`) so the pure-view
// fold stays synchronous where there is no renderer. Each scheduled card is held
// in `_drawAnimIds` so a re-fold before its beat doesn't double-schedule it;
// on landing it degrades gracefully if it already arrived or the server no
// longer holds it (e.g. cast/discarded mid-stagger).
export function scheduleHandAdditions(host, additions, { handKey, seat, current = [] }) {
  if (typeof host._animateDrawnCardToHand !== 'function') {
    host.setState({ [handKey]: [...current, ...additions] });
    host._maybeRunOpeningDraw?.();
    return;
  }
  const inFlight = host._drawAnimIds || (host._drawAnimIds = new Set());
  additions.forEach((card, i) => {
    inFlight.add(card.id);
    setTimeout(() => {
      inFlight.delete(card.id);
      if (host._unmounted) return;
      // Replay only: the seat can move under the timer (a viewpoint flip
      // re-folds the hand for the other seat in place), and a landing for a
      // seat that is no longer ours would append that seat's card to the new
      // hand (QA#151). Live seats never move once resolved.
      if (host.props?.replayDriver
        && !ownSeatViews(host).some((v) => v.seat === seat && v.handKey === handKey)) return;
      const cur = host.state?.[handKey] || [];
      if (cur.some((c) => c.id === card.id)) return; // already landed
      const mirrorHand = host._authMirror?.getState?.()?.hands?.[seat];
      if (Array.isArray(mirrorHand) && !mirrorHand.includes(card.id)) return; // left before its beat
      host.setState((s) => ({ [handKey]: [...(s[handKey] || []), card] }), () => {
        // The mulligan-open gate waits for the rendered hand to catch up to the
        // mirror, but the staggered landings happen on TIMERS. The server is
        // idle after the deal, so no further diff will re-run the fold check.
        // Re-check here as each card lands or the prompt never opens.
        host._maybeRunOpeningDraw?.();
      });
      host._animateDrawnCardToHand(card, seat);
    }, i * DRAW_STAGGER_MS);
  });
}

// LIVE own-hand LEAVES (pure-view): the complement of the additions fold. A
// server-paid hand discard (Sisters of Avalon's Genesis cost, Wizard's Den's
// hand pick) moves the card hand → cemetery entirely server-side; the pile
// reconciler renders the cemetery copy, but nothing dropped the hand render,
// so the card ghosted in the fan until a reload (qa#81). Conservative on the
// one-place invariant: a hand card is dropped ONLY when the mirror explicitly
// places it in a non-hand zone AND it is already rendered elsewhere (a pile
// render or a board mesh). An optimistic local pickup (sendToHand's play→hand
// intent still in flight, mirror still saying 'play', rendered ONLY in hand)
// is therefore never fought.
export function applyHandLeavesFromMirror(host, mirrorState) {
  const cards = mirrorState?.cards;
  if (!cards) return;
  const gs = host.state?.gameState;
  for (const { handKey } of ownSeatViews(host)) {
    const current = host.state?.[handKey] || [];
    if (current.length === 0) continue;
    const kept = current.filter((c) => {
      const mc = cards[c.id];
      if (!mc?.zone || mc.zone === 'hand') return true; // no opinion / still in hand
      const renderedElsewhere = (mc.zone === 'held' && isUnitHandSpell(c.id, mirrorState, gs))
        || host.meshes?.has?.(c.id)
        || (gs?.piles || []).some((p) => Array.isArray(p.cards) && p.cards.some((pc) => pc.id === c.id));
      return !renderedElsewhere;
    });
    if (kept.length !== current.length) host.setState({ [handKey]: kept });
  }
}

// P-A. Reconnect-authoritative rebuild of the LOCAL player's own piles from
// the mirror. After a reconnect the legacy snapshot can leave the owner's deck/
// cemetery/banished/collection cards art-less or empty (the "image missing" /
// empty-pile bug), because they were drawn through paths that didn't carry
// identity. The server holds the authoritative zone contents (R2c) + the per-
// seat projection keeps OUR own zones in full, so rebuild each pile's cards from
// the mirror, resolving cardId → catalog for the art. Fires on a `full` (the
// reconnect/resume signal); a no-op in live play (cards already identified +
// order matches). Cornerstone of the dumb client (render zones from the mirror).
export function zoneToPileName(zone, rotated) {
  if (zone === 'spellbook') return 'Spellbook';
  if (zone === 'atlas') return 'Atlas';
  if (zone === 'collection') return 'Collection';
  if (zone === 'cemetery') return rotated ? 'Cemetery P2' : 'Cemetery P1';
  if (zone === 'banished') return rotated ? 'Banished P2' : 'Banished P1';
  return null;
}

function createDrawPileFromMirror(host, zone, rotated) {
  // A cold renderer has no locally-spawned deck shells. The projection is
  // sufficient to materialize them: identities for an owned pile, counts for
  // a hidden pile, and the configured spawn point for presentation only.
  const name = zoneToPileName(zone, rotated);
  if (name !== 'Spellbook' && name !== 'Atlas') return null;
  const point = getSpawnPoint(host.state?.spawnConfig, `${zone}${rotated ? '2' : ''}`);
  const pile = createPile(name, [], point.x, point.z, rotated);
  host.state.gameState.piles.push(pile);
  return pile;
}

export function applyOwnPilesFromMirror(host, mirrorState) {
  // Replay: full information. The omniscient state carries BOTH seats' real
  // pile contents, so both fold as own piles. Letting the opposite seat run
  // the count-placeholder path instead makes it fight the pile reconciler
  // (placeholder ids never match the real cards the reconciler re-adds every
  // fold), and the stack grows without bound.
  const views = host.props?.replayDriver
    ? [{ seat: 'p1', rotated: false }, { seat: 'p2', rotated: true }]
    : ownSeatViews(host);
  for (const { seat, rotated } of views) {
    applyOwnPilesForSeat(host, mirrorState, seat, rotated);
  }
}

function applyOwnPilesForSeat(host, mirrorState, seat, localRotated) {
  const gs = host.state?.gameState;
  const seatPiles = mirrorState?.piles?.[seat];
  const cards = mirrorState?.cards;
  if (!gs?.piles || !seatPiles || !cards) return;

  const byId = host._sorceryCardIndex
    || (host._sorceryCardIndex = new Map((host.props.sorceryCards || []).map((c) => [c.unique_id, c])));
  const resolve = (id) => {
    const mc = cards[id];
    if (!mc || mc.cardId == null) return null;
    const cat = byId.get(mc.cardId);
    return cat ? instanceFromMirror(cat, mc, localRotated, id) : null;
  };

  let changed = false;
  for (const [zone, ids] of Object.entries(seatPiles)) {
    if (zone === 'tokens' || !Array.isArray(ids)) continue;
    const name = zoneToPileName(zone, localRotated);
    if (!name) continue;
    let pile = gs.piles.find((p) => p.name === name && !!p.rotated === localRotated);
    if (!pile) {
      if (ids.length === 0) continue; // empty + not spawned. Nothing to render
      // The lazily-materialized piles (collection/cemetery/banished) aren't
      // re-spawned on reload, so create the local one from the mirror (else our
      // own Collection vanishes after a reconnect even though the server holds it).
      if (zone === 'collection') pile = host.findOrCreateCollection?.(localRotated);
      else if (zone === 'cemetery') pile = host.findOrCreateCemetery?.(localRotated);
      else if (zone === 'banished') pile = host.findOrCreateBanished?.(localRotated);
      else pile = createDrawPileFromMirror(host, zone, localRotated);
      if (!pile) continue;
      changed = true;
    }
    // For a draw pile the mirror is top-to-bottom and the render array is
    // bottom-to-top. Invert both the comparison and the rebuild, or every fold
    // re-churns the pile. Arrival-ordered piles share the render order already.
    const wantIds = isTopIndexedZone(zone) ? renderOrderFromMirror(ids) : ids;
    const haveIds = pile.cards.map((c) => c.id);
    const sameOrder = haveIds.length === wantIds.length && wantIds.every((id, i) => haveIds[i] === id);
    const allIdentified = pile.cards.every((c) => c.cardId && (c.imageUrl || c.imagePath));
    if (sameOrder && allIdentified) continue; // already correct
    const rebuilt = wantIds.map(resolve).filter(Boolean);
    if (rebuilt.length !== wantIds.length) continue; // catalog miss. Never wipe a pile
    pile.cards = rebuilt; // mutate in place to keep mesh.userData.pile refs valid
    changed = true;
  }
  if (changed) {
    host.updatePileMeshes?.();
    host.forceUpdate?.();
  }
}

// Render reconciler (pure-view binding): keep the rendered PILES a function of the
// authoritative card zones on EVERY diff. A card the mirror has moved OUT of a
// pile it is still rendered in is dropped from that pile render. Closing the
// opponent-public-pile-leave gap (the opponent pulls a card from THEIR cemetery →
// our cemetery render must drop it live, not wait for a reload's full rebuild; the
// cemetery-pull desync). Runs for BOTH seats' visible piles, so it self-heals any
// missing incremental fold regardless of source. Remove-only: cards ENTERING a
// pile are placed by applyBoardExitsFromMirror (board→pile) / applyOwnPilesFrom-
// Mirror (own, on Full). Conservative: a card the mirror has no opinion on (no
// entry / no zone. A local-only token, a not-yet-synced card) is LEFT in place,
// so this can only correct a drift the server explicitly disagrees with, never
// wrongly wipe a pile.
export function reconcilePileRenderToMirror(host, mirrorState) {
  const cards = mirrorState?.cards;
  const gs = host.state?.gameState;
  if (!cards || !Array.isArray(gs?.piles)) return;
  const localSeat = host.state?.isHost ? 'p1' : 'p2';
  const localRotated = !host.state?.isHost;
  const byId = host._sorceryCardIndex
    || (host._sorceryCardIndex = new Map((host.props.sorceryCards || []).map((c) => [c.unique_id, c])));
  let changed = false;

  // REMOVE: drop rendered pile cards the mirror moved OUT of that pile.
  for (const pile of gs.piles) {
    if (!Array.isArray(pile?.cards) || pile.cards.length === 0) continue;
    const kept = pile.cards.filter((c) => {
      const mc = cards[c.id];
      if (!mc || !mc.zone) return true; // no authoritative opinion → leave it
      return zoneToPileName(mc.zone, !!pile.rotated) === pile.name;
    });
    if (kept.length !== pile.cards.length) { pile.cards = kept; changed = true; }
  }

  // ADD: place a card the mirror has in a VISIBLE pile that isn't rendered there
  // yet (a unit that died into a cemetery, a token to banished). Card-zone-driven
  // so it covers the OPPONENT's public piles too (projection deletes piles[opp]
  // but keeps the public card with its zone). Skip cards still rendered as a live
  // board mesh (their board-exit flight is mid-flight) to avoid a double render;
  // the next diff places them once the flight has cleared the mesh. This is the
  // complement to applyBoardExitsFromMirror (board→pile flight) and the safety net
  // when that flight fails to land the card in the pile data (the "left the board,
  // never showed in cemetery until reload" bug).
  for (const [id, mc] of Object.entries(cards)) {
    if (!mc?.zone || mc.cardId == null) continue;
    if (host.meshes?.has(id)) continue; // still a board mesh (mid board-exit). Defer
    const rotated = (mc.owner === localSeat) ? localRotated : !localRotated;
    const name = zoneToPileName(mc.zone, rotated);
    if (!name) continue; // not a pile zone (play / hand / storyline)
    let pile = gs.piles.find((p) => p.name === name && !!p.rotated === rotated);
    if (!pile) {
      if (mc.zone === 'cemetery') pile = host.findOrCreateCemetery?.(rotated);
      else if (mc.zone === 'banished') pile = host.findOrCreateBanished?.(rotated);
      else if (mc.zone === 'collection') pile = host.findOrCreateCollection?.(rotated);
      else pile = createDrawPileFromMirror(host, mc.zone, rotated);
      if (!pile) continue;
    }
    if (pile.cards.some((c) => c.id === id)) continue; // already rendered
    const cat = byId.get(mc.cardId);
    if (!cat) continue; // catalog miss. Never render a blank
    const inst = instanceFromMirror(cat, mc, rotated, id);
    // Place it where the MIRROR says it sits, inverted into render order. The
    // projection keeps our OWN pile arrays (it only deletes the opponent's), so
    // for an order-bearing draw pile we know the exact slot; without this a card
    // the server put on the bottom (a mulligan return) lands on the render TOP
    // and is the next card drawn (GH-76). Arrival-ordered piles (cemetery,
    // banished) and opponent public piles with no mirror array. Append, as
    // before, which already agrees with the mirror's own arrival order.
    const mirrorIds = isTopIndexedZone(mc.zone) ? mirrorState?.piles?.[mc.owner]?.[mc.zone] : null;
    const mi = Array.isArray(mirrorIds) ? mirrorIds.indexOf(id) : -1;
    if (mi === -1) {
      pile.cards.push(inst);
    } else {
      // Render index = how many already-rendered cards sit BELOW it, i.e. those
      // deeper in the mirror. Robust to a partially-rendered pile.
      const below = pile.cards.filter((c) => {
        const j = mirrorIds.indexOf(c.id);
        return j !== -1 && j > mi;
      }).length;
      pile.cards.splice(below, 0, inst);
    }
    changed = true;
  }

  if (changed) {
    host.updatePileMeshes?.();
    host.forceUpdate?.();
  }
}

// Order adopter (#53). The companion to reconcilePileRenderToMirror, which is
// MEMBERSHIP-only. The server owns every shuffle: P7 discards whatever order the
// client asserts and commits its own crypto permutation. That committed order
// only ever reached the render through applyOwnPilesFromMirror, which runs on a
// `full`. So after a mid-match shuffle the render kept the client's local
// Fisher-Yates while truth held a different order. The two only reconverged at
// the next reload, which is why the same "known" top cards kept coming back.
//
// Runs on EVERY diff for the local seat's top-indexed (draw) piles, and does
// nothing but permute: it never adds, removes or re-creates a card. The card
// INSTANCES are reused by id, so mesh.userData.pile refs, art and flags survive
// untouched (no despawn/respawn flicker on a shuffle).
//
// The id-set equality guard is load-bearing. An optimistic local move (a draw
// already pulled from the render, not yet confirmed) makes the sets differ, and
// reordering then would resurrect or drop a card. So we skip and let the diff
// that confirms the move bring the sets back together.
export function adoptOwnPileOrderFromMirror(host, mirrorState) {
  const gs = host.state?.gameState;
  if (!gs?.piles || !mirrorState?.piles) return;
  let changed = false;

  for (const { seat, rotated } of ownSeatViews(host)) {
    const seatPiles = mirrorState.piles[seat];
    if (!seatPiles) continue;
    for (const [zone, ids] of Object.entries(seatPiles)) {
      if (!Array.isArray(ids) || !isTopIndexedZone(zone)) continue;
      const name = zoneToPileName(zone, rotated);
      const pile = gs.piles.find((p) => p.name === name && !!p.rotated === rotated);
      if (!pile || !Array.isArray(pile.cards)) continue;
      if (pile.cards.length !== ids.length) continue; // membership differs. Not ours to fix
      const byInstanceId = new Map(pile.cards.map((c) => [c.id, c]));
      // Same length AND every mirror id present == equal id sets (a Map keyed on
      // id also rules out a duplicate-id render, which is not a permutation).
      if (byInstanceId.size !== ids.length || !ids.every((id) => byInstanceId.has(id))) continue;
      const want = renderOrderFromMirror(ids);
      if (want.every((id, i) => pile.cards[i].id === id)) continue; // already agrees
      pile.cards = want.map((id) => byInstanceId.get(id));
      changed = true;
    }
  }

  if (changed) {
    host.updatePileMeshes?.();
    host.forceUpdate?.();
  }
}

// Mirror-driven storyline reconcile. The storyline is a PUBLIC server zone, so
// BOTH clients render the dwelling cast + its prompt from the mirror. Under
// auth this REPLACES the legacy `storyline:cast` P2P relay (which is suppressed
// on the receiving side so the ack isn't doubled). Runs on every fold:
//   - Opponent's cast: render the dwelling card on our storyline + a read-only
//     acknowledge, so the non-caster actually SEES the public cast (live and
//     after reload). The visual is reconciler-managed (`_mirrorStorylineVisuals`)
//     and removed when the card leaves the storyline (→ its square).
//   - Our own cast: live, the local cast flow owns the visual + prompt. Only on
//     a Full (reload) do we re-derive. Re-run on_play to re-open the decision /
//     acknowledge (which also wires the landing). on_play has NOT applied while
//     the card dwells, so the re-run is its first and only application.
// Idempotent: `_storylineRehydrated` guards the own-card re-derive, the visual
// set guards the opponent render.
export function syncStorylineFromMirror(host, mirrorState, { isFull = false } = {}) {
  const cards = mirrorState?.cards;
  if (!cards) return;
  const localSeat = host.state?.isHost ? 'p1' : 'p2';
  const byId = host._sorceryCardIndex
    || (host._sorceryCardIndex = new Map((host.props.sorceryCards || []).map((c) => [c.unique_id, c])));
  if (!host._storylineRehydrated) host._storylineRehydrated = new Set();
  if (!host._mirrorStorylineVisuals) host._mirrorStorylineVisuals = new Set();

  const onStoryline = new Set();
  for (const [id, mc] of Object.entries(cards)) {
    if (mc?.zone !== 'storyline' || mc.cardId == null) continue;
    onStoryline.add(id);
    const cat = byId.get(mc.cardId);
    if (!cat) continue;
    const owner = mc.owner;
    const rotated = owner === 'p2';

    // Solo: the pilot IS both seats. Every storyline cast is its own (the
    // local cast flow owns the visual + prompt; only a Full re-derives).
    // Replay: NO cast is the viewer's own. The own branch re-runs on_play
    // automation and stages resolution prompts; both seats take the
    // visual-only path (dwell art + reconciler) instead.
    if (!host.props?.replayDriver && (host.props?.isCasualSandbox || owner === localSeat)) {
      if (!isFull) continue; // live: the local cast flow owns it
      if (host._storylineRehydrated.has(id)) continue;
      if (host._spellPlayInFlight?.has(id)) continue;
      if ((host.state.pendingPrompts || []).some((p) => p?.cardInstance?.id === id)) continue;
      host._storylineRehydrated.add(id);
      const cardInstance = instanceFromMirror(cat, mc, rotated, id);
      // Restore the cast cell the spell has to land on. It was committed at
      // the cast (castMagicFromHand), before the disconnect, and is the only
      // record of where the drag dropped it.
      const world = mc.cell ? host._cellCenterWorld?.(mc.cell.col, mc.cell.row) : null;
      if (world) { cardInstance.x = world.x; cardInstance.z = world.z; }
      if (!host._castingSpellCards) host._castingSpellCards = {};
      host._castingSpellCards[id] = cardInstance;
      // Land it before the on_play re-run, same ordering as a live cast. The
      // spell is a board card whose effect happens to still be resolving.
      host._landCastSpellOnBoard?.(cardInstance);
      host.applyCardAutomation?.(cardInstance, ['on_play'], { controller: owner });
      if (!host._spellPlayInFlight?.has(id)) delete host._castingSpellCards[id];
    } else {
      if (host._mirrorStorylineVisuals.has(id)) continue;
      host._mirrorStorylineVisuals.add(id);
      const cardInstance = instanceFromMirror(cat, mc, rotated, id);
      const eid = `cast-${id}`;
      host.setState((s) => {
        if ((s.castingSpells || []).some((f) => f.id === eid)) return null;
        return { castingSpells: [...(s.castingSpells || []), { id: eid, cardName: cat.name || '', imageUrl: cardInstance.imageUrl || null, rotated }] };
      });
      // The ack entry is a prompt (OK button in the effect drawer); a replay
      // viewer acknowledges nothing, the dwell art alone tells the story.
      if (!host.props?.replayDriver) {
        host._enqueueOpponentSpellNotice?.({ cardId: id, cardName: cat.name || t('a spell'), imageUrl
: cardInstance.imageUrl || null, controller: owner, cardInstance });
      }
    }
  }

  // Drop reconciler-managed (opponent) visuals whose card LEFT the storyline.
  // The caster's own visuals are owned by the local cast flow, never in this set.
  for (const id of [...host._mirrorStorylineVisuals]) {
    if (onStoryline.has(id)) continue;
    host._mirrorStorylineVisuals.delete(id);
    const eid = `cast-${id}`;
    host.setState((s) => ({ castingSpells: (s.castingSpells || []).filter((f) => f.id !== eid) }));
  }
}

// Positions domain (D5) reconnect fold. The server holds each in-play card's
// resting coords; on a Full (reconnect/resume) we relocate every play-zone mesh
// to its authoritative {x,z}, REPLACING the legacy host-blob position. Live
// moves still ride card:release (P2P). This only runs on restore. Keeps the
// card's current orientation (derived from the authoritative tapped/rotated/
// subsurface flags); only X/Z move. Idempotent: a card already at its coords is
// skipped so a repeated Full doesn't churn the physics body.
// Reconnect board rebuild (Domain 5). The server's authoritative MatchState
// holds every in-play card by instanceId with { cardId, pos, cell, owner,
// controller, tapped, rotated, isSite }. But NOT name/type/stats (those are
// catalog-derived). On a reconnect Full the legacy snapshot only restores the
// dealt board (avatars), so cards PLAYED during the match vanish. This recreates
// their meshes from the mirror: cardId → catalog → createCardInstance →
// addCardToTable (broadcast=false, no on_play re-fire). The idempotency guard in
// addCardToTable skips cards that already have a mesh (avatars, live cards), so
// this only fills the gaps. applyPositionsFromMirror (called right after) snaps
// each rebuilt mesh to its exact authoritative coords.
export function rebuildBoardFromMirror(host, mirrorState, opts = {}) {
  const cards = mirrorState?.cards;
  if (!cards) return;
  // Carried cards (an Omphalos / artifact worn by a site or minion) live in the
  // registry BOTH as their own zone:'play' entry AND inside their bearer's
  // equippedCards. The board surfaces them only as carried (the bearer's
  // equipment panel), so a reload rebuild must NOT also spawn a loose standalone
  // mesh for them. That double-renders the card on the bearer's cell (the
  // "second copy on the same square after reload" bug).
  const carriedIds = new Set();
  for (const mc of Object.values(cards)) {
    if (!Array.isArray(mc?.equippedCards)) continue;
    for (const eq of mc.equippedCards) if (eq?.id) carriedIds.add(eq.id);
  }
  const byId = host._sorceryCardIndex
    || (host._sorceryCardIndex = new Map((host.props.sorceryCards || []).map((c) => [c.unique_id, c])));
  const poses = restPosesFromMirror(host, mirrorState);
  let added = false;
  for (const [id, mc] of Object.entries(cards)) {
    if (mc?.zone !== 'play' || mc.cardId == null) continue;
    if (carriedIds.has(id)) continue; // worn by a bearer. Surfaced as carried, not loose
    if (host.meshes.has(id)) continue; // already rendered (avatars / live cards)
    // LIVE diff spawn: any zone-play card with no local mesh renders from the
    // diff. Server-minted tokens AND cast cards (the opponent's authoritative
    // view of a play must never depend on the legacy card:place relay racing
    // the diff). The relay double is handled by idempotency on both sides:
    // this loop skips existing meshes, and addCardToTable skips existing ids
    // when the relay's card:place lands second.
    const cat = byId.get(mc.cardId);
    if (!cat) continue;
    const rotated = mc.rotated ?? (mc.owner === 'p2');
    const card = instanceFromMirror(cat, mc, rotated, id);
    card.controller = mc.controller || (rotated ? 'p2' : 'p1');
    card.tapped = !!mc.tapped;
    card.faceDown = !!mc.faceDown;
    if (mc.cell) card.cell = mc.cell;
    if (mc.stackSeq !== undefined) card.stackSeq = mc.stackSeq;
    // Spawn coords are derived from the server cell (per-cell cascade; sites
    // snap), not the stored free pos. The pos is only a fallback when the
    // cell can't be resolved (no grid / pre-position avatar / aura).
    const rpos = cat.type === 'Aura' ? null : poses.get(id);
    if (rpos) { card.x = rpos.x; card.z = rpos.z; }
    else if (mc.pos) { card.x = mc.pos.x; card.z = mc.pos.z; }
    else if (cat.type === 'Avatar') {
      // The dealt avatar has no cell or pos until its owner first drops it,
      // so a replay's frame 0 (or a spectator Full) would park both avatars
      // at the world origin, overlapping. Seat it where the live deck spawn
      // does, on its side's avatar spawn point.
      const p = getSpawnPoint(host.state?.spawnConfig, `avatar${rotated ? '2' : ''}`);
      card.x = p.x; card.z = p.z;
    }
    if (mc.subsurface !== undefined) card.subsurface = mc.subsurface;
    if (mc.isSite) card.isSite = true;
    host.addCardToTable(card, false);
    added = true;
  }
  if (added) {
    host.recomputeStaticAuras?.();
    host.forceUpdate?.();
  }
}

// Solo Full scrub (pure-view binding). A solo RESUME mounts sessionMode 'new'
// and spawns the re-minted room's decks (fresh prepared-deck ids), then folds
// the durable Full (the ORIGINAL match ids) over them. Hands and pile orders
// are rebuilt wholesale from the mirror, but a freshly spawned BOARD mesh the
// mirror has never heard of (the dealt avatars) would linger as a stacked
// phantom copy of its server-restored twin. In solo the pilot owns both seats
// and every board card is server-minted, so on a Full the mirror is the
// complete board truth: drop any in-play mesh whose id the mirror doesn't
// know at all. Skips mid-drag / ownership-claimed meshes; ids the mirror DOES
// know (any zone, incl. in-flight casts sitting in 'hand') are owned by the
// entry/exit folds above, never scrubbed here.
export function scrubUnknownBoardMeshesFromMirror(host, mirrorState) {
  const cards = mirrorState?.cards;
  if (!cards) return;
  for (const [id, mesh] of [...host.meshes]) {
    if (cards[id]) continue;
    const card = mesh.userData?.cardInstance;
    if (!card) continue;
    if (host.dragging?.members?.some?.((dm) => dm.mesh === mesh)) continue;
    if (host.ownership && !host.ownership.is(id, 'free')) continue;
    host.removeCardFromTable(card, false);
  }
}

// Server-driven board exits (death / banish). The server moves a killed unit to
// its owner's cemetery (or a token to banished); the diff only flips the card's
// zone field. This detects an in-play LOCAL mesh whose mirror zone is now a pile
// and flies it there via the normal cemetery animation. FromMirror suppresses
// the re-broadcast (the server already owns the move). queueDeparture staggers
// the flight behind an active combat scene so the strike plays first, then the
// card leaves. Without this a server-killed card lingers on the board until the
// reconciler force-disposes it (the "randomly disappeared" bug).
const ZONE_TO_PILE = { cemetery: 'Cemetery', banished: 'Banished' };

export function applyBoardExitsFromMirror(host, mirrorState) {
  const cards = mirrorState?.cards;
  if (!cards) return;
  // Last mirror zone folded per card. A hand-exit is a play→hand TRANSITION.
  // a mesh existing while the mirror says 'hand' with NO prior 'play' is an
  // in-flight cast (either seat: the actor's optimistic mesh, or the
  // opponent's relay-staged mesh), and yanking it was the play-bounce /
  // opponent-invisible-card bug. Seeded at the end of every fold.
  const seen = host._mirrorZoneSeen || (host._mirrorZoneSeen = new Map());
  for (const [id, mesh] of [...host.meshes]) {
    const mc = cards[id];
    if (!mc || mc.zone === 'play') continue;
    const card = mesh.userData?.cardInstance;
    if (!card) continue;
    // Server returned a board card to its owner's hand (return_self_to_hand /
    // return_each_to_hand / sendToHand). Render-only. The server already
    // recorded the move: drop the mesh, and if it's OUR card add it to our hand
    // fan (an opponent card's hand count comes from applyZonesFromMirror).
    if (mc.zone === 'hand') {
      // A meshed card the mirror has NEVER shown in play is an in-flight cast
      // (either seat's optimistic / relay-staged mesh), not a server exit.
      // bouncing it was the play-bounce bug. The pending-cast sweep (S5) owns
      // an actor cast's confirm-or-revert; here we only require the play→hand
      // TRANSITION. An opponent's in-flight cast and its genuine return-to-hand
      // both project as a skeleton in zone 'hand', so the transition history is
      // the only signal that separates them.
      if (seen.get(id) !== 'play') continue;
      // A mid-DRAG staged card (lifted from hand, not yet committed) is not a
      // server exit either. Folding a diff must never kill an active drag.
      if (host.dragging?.members?.some?.((dm) => dm.mesh === mesh)) continue;
      host.removeCardFromTable(card, false);
      if (host.isCardOnLocalSide(card)) host.addToHand(card);
      continue;
    }
    const pileName = ZONE_TO_PILE[mc.zone];
    if (!pileName) continue;
    host.sendCardToPile(card, pileName, false, 'top', { fromMirror: true, queueDeparture: true });
  }
  // Seed the transition tracker from the FULL mirror (cards without meshes
  // included, so a card that appears via the entries fold has a known prior).
  for (const [id, mc] of Object.entries(cards)) seen.set(id, mc?.zone);
}

// The complement of the carriedIds skip above. A card that becomes carried
// keeps zone:'play' (equip emits SET_EQUIPMENT on the bearer, never a zone
// move), so applyBoardExitsFromMirror never sees it leave and nothing folded
// from the mirror ever removed its loose mesh. Only the unsequenced
// card:remove relay did. On the opponent a diff landing between that relay
// and the SET_EQUIPMENT diff respawns the artifact on its old cell via
// rebuildBoardFromMirror (carriedIds still empty), and the equip diff then
// leaves that resurrected mesh in place for good (the "helmet still on the
// site for my opponent" bug). A replay has no relay at all, so every
// equipped artifact rendered twice there (site AND bearer). Runs before the
// entries fold so the render is a pure function of the mirror either way.
//
// TRANSITION-driven, like the hand exit above: only an id that is newly
// carried this fold loses its mesh. A local detach spawns the dropped card
// and emits SET_POSITION / SET_SUBSURFACE before its SET_EQUIPMENT, so for a
// diff or two the mirror still lists the card as carried while a legitimate
// loose mesh exists. Removing it there would flicker the drop and then
// respawn the artifact on its pre-equip cell.
export function applyCarriedExitsFromMirror(host, mirrorState) {
  const cards = mirrorState?.cards;
  if (!cards) return;
  const seen = host._mirrorCarriedSeen || (host._mirrorCarriedSeen = new Set());
  const now = new Set();
  for (const mc of Object.values(cards)) {
    if (!Array.isArray(mc?.equippedCards)) continue;
    for (const eq of mc.equippedCards) if (eq?.id) now.add(eq.id);
  }
  // Ids handled this fold. A drag / ownership-claimed skip is deliberately
  // NOT recorded, so the still-loose mesh is retried on the next fold.
  const next = new Set();
  for (const id of now) {
    if (seen.has(id)) { next.add(id); continue; } // carried last fold too: a mesh now is a local detach in flight
    const mesh = host.meshes.get(id);
    const card = mesh?.userData?.cardInstance;
    if (!card) { next.add(id); continue; }
    if (host.dragging?.members?.some?.((dm) => dm.mesh === mesh)) continue;
    if (host.ownership && !host.ownership.is(id, 'free')) continue;
    // Same options the local equip path uses: no relay echo, and the
    // artifact's own sub-equipment stays attached rather than dropping
    // to the field.
    host.removeCardFromTable(card, false, { detachEquipmentToField: false });
    next.add(id);
  }
  host._mirrorCarriedSeen = next;
}

// Cell-authoritative render position (Positions domain D5 / S2). A card's board
// {x,z} is a pure function of its server cell. NOT the stored free pos:
//   - Sites snap to the controller's near edge of the cell (unchanged).
//   - Every other non-Aura card sits at the cell CENTER plus a small
//     deterministic per-id offset, so multiple cards on one cell peek out of the
//     stack rather than perfectly overlapping. The tidy-stack / fan treatment
//     lands in S3; here the offset only breaks the exact overlap.
//   - Auras anchor to grid INTERSECTIONS, a position no single cell center can
//     express, so the caller flags them (isAura) and they keep their stored pos.
// The offset angle is a hash of the instanceId (never random) and the radius is
// a fixed fraction (<1/3) of the cell, so the point always resolves back to the
// same cell (render-parity holds) and is identical on both clients. Returns null
// when the cell can't be resolved (no grid / no cell / aura). The caller then
// falls back to the stored pos.
// Resting poses for EVERY in-play card in the mirror, one layoutCellRest call
// per occupied cell (the cascade depends on ALL of a cell's members, so
// positions can only be computed per cell, never per card). Member facts come
// from the mirror first (present even for hidden-card skeletons: cell,
// stackSeq, subsurface, isSite are all public) with the live client card as
// tie-breaker for locally-known state. Auras are excluded. They anchor to
// grid INTERSECTIONS, which no cell layout can express, and keep their stored
// pos. Returns Map(id -> {x, z, y, rank}).
export function restPosesFromMirror(host, mirrorState) {
  const cards = mirrorState?.cards;
  const grid = getGameGrid(host.state?.spawnConfig);
  if (!cards || !grid) return new Map();
  const byId = host._sorceryCardIndex
    || (host._sorceryCardIndex = new Map((host.props.sorceryCards || []).map((c) => [c.unique_id, c])));
  const groups = new Map();
  for (const [id, mc] of Object.entries(cards)) {
    if (mc?.zone !== 'play') continue;
    const col = mc?.cell?.col;
    const row = mc?.cell?.row;
    if (typeof col !== 'number' || typeof row !== 'number') continue;
    const card = host.meshes.get(id)?.userData?.cardInstance;
    const type = card?.type ?? byId.get(mc.cardId)?.type;
    if (type === 'Aura') continue;
    const key = `${col},${row}`;
    let arr = groups.get(key);
    if (!arr) groups.set(key, (arr = []));
    arr.push({
      id,
      isSite: card ? !!card.isSite : (!!mc.isSite || type === 'Site'),
      isToken: !!(mc.isToken ?? card?.isToken),
      subsurface: !!(mc.subsurface ?? card?.subsurface),
      stackSeq: mc.stackSeq,
      rotated: card ? !!card.rotated : (mc.rotated ?? (mc.owner === 'p2')),
      tapped: !!(mc.tapped ?? card?.tapped),
    });
  }
  const subsurfaceView = host.state.regionView === 'subsurface';
  const out = new Map();
  for (const [key, members] of groups) {
    const [colStr, rowStr] = key.split(',');
    const geo = sampleCellGeometry(grid, parseInt(colStr, 10), parseInt(rowStr, 10));
    if (!geo) continue;
    for (const [id, pose] of layoutCellRest(geo, members, { subsurfaceView })) out.set(id, pose);
  }
  return out;
}

export function applyPositionsFromMirror(host, mirrorState) {
  foldPositionsFromMirror(host, mirrorState, null);
}

// Live-diff variant: folds server-driven in-play moves (Wildfire spread,
// Thunderstorm/Bladedancer/persecutor steps, group teleports) so they render
// without a reconnect Full. Skips any card under an active drag or ownership
// claim. The cosmetic presence relay (claim/stream/release) still owns
// player drags and their settled landing; server-initiated moves never claim,
// so the guard cleanly separates the two.
export function applyLivePositionsFromMirror(host, mirrorState) {
  foldPositionsFromMirror(host, mirrorState, (id, mesh) => {
    if (host.ownership && !host.ownership.is(id, 'free')) return true;
    // In-flight optimistic movement (move intent / SET_POSITION echo not yet
    // folded): the mirror still holds the pre-move slot, so folding it would
    // glide the card back to where it came from and then forward again when
    // the confirm lands (the move/reposition bounce). The confirming diff
    // (or the TTL sweep on a reject) ends the hold.
    if (host._isMoveInFlight?.(id, mirrorState) || host._isNudgeInFlight?.(id, mirrorState)) return true;
    return !!host.dragging?.members?.some?.((dm) => dm.mesh === mesh);
  });
}

function foldPositionsFromMirror(host, mirrorState, skip) {
  const cards = mirrorState?.cards;
  if (!cards) return;
  const grid = getGameGrid(host.state?.spawnConfig);
  // Per-cell cascade layout: one pose map for the whole board, recomputed on
  // every fold. A card arriving on / leaving a cell shifts its former and new
  // neighbours' cascade slots too, so per-card folding would go stale.
  const poses = restPosesFromMirror(host, mirrorState);
  // Focused-cell fan (cell → fan → act): the pose of a focused cell's members
  // is the FAN slot, not the resting cascade. Pure function of
  // (state, focusedCell), so a diff mid-fan folds INTO the fan and there is
  // never a saved pose to go stale.
  for (const [id, pose] of fanPosesForFocusedCell(host)) poses.set(id, pose);
  let moved = false;
  for (const [id, mc] of Object.entries(cards)) {
    if (mc?.zone !== 'play') continue;
    const mesh = host.meshes.get(id);
    if (!mesh) continue;
    const card = mesh.userData?.cardInstance;
    // The authoritative cell + stackSeq + subsurface ride the position fold
    // onto the client card instance. The per-frame Y pass, the cell focus
    // strip, and the hover highlight all group by card.cell, and
    // rebuildBoardFromMirror only stamps cards IT creates (a dealt avatar / a
    // card you played yourself never passes through it). Stamped BEFORE the
    // ownership/drag skip: a cast's only diff usually lands inside the claim
    // window, and skipping the stamp there left the fields stale forever.
    // the two clients then ranked the same cell differently (the
    // "buried under the site" divergence). Only the POSE is claim-guarded.
    if (card && mc.cell) card.cell = { col: mc.cell.col, row: mc.cell.row };
    if (card && mc.stackSeq !== undefined) card.stackSeq = mc.stackSeq;
    if (card && mc.subsurface !== undefined) card.subsurface = mc.subsurface;
    if (skip?.(id, mesh)) continue;
    // Render position is a pure function of the server CELL (per-cell cascade
    // by stackSeq; sites snap to their edge). Auras anchor to grid
    // intersections and keep their stored pos. When the cell can't be resolved
    // (no grid / pre-position avatar / aura) fall back to the pos.
    // Free card placement (manual session, or the local snap-to-grid setting
    // off): a non-site card with server-stored coords rests exactly where it
    // was dropped. The pos IS the layout, no cell-cascade snap. Sites keep
    // the grid: the realm IS the map. A card without stored coords (opponent
    // snapping client, server-forced move) falls back to the cell cascade.
    const isAura = card?.type === 'Aura';
    const freePlaced = (host._freeCardPlacement?.() || host._automationsEnabled?.() === false)
      && !card?.isSite && card?.type !== 'Site'
      && mc.pos && typeof mc.pos.x === 'number' && typeof mc.pos.z === 'number';
    const rpos = (isAura || freePlaced) ? null : poses.get(id);
    let x, z;
    if (rpos) {
      ({ x, z } = rpos);
    } else if (mc.pos && typeof mc.pos.x === 'number' && typeof mc.pos.z === 'number') {
      ({ x, z } = mc.pos);
    } else {
      continue;
    }
    if (card && Math.abs((card.x ?? 0) - x) < 0.01 && Math.abs((card.z ?? 0) - z) < 0.01) continue;
    // Cosmetic glide to the new slot (fan out/in, cascade re-slot, a
    // server-confirmed move). The pose target is authoritative immediately
    // (card.x/z below), only the mesh's transit is animated.
    glideMeshXZ(host, mesh, x, z);
    if (card) {
      card.x = x;
      card.z = z;
      // Re-derive the aura anchor from the new position (the card:release path
      // did this; the equipment-lock set + 'covered' scope read it). Keeps both
      // clients' aura footprints identical now positions sync via the server.
      if (isAura && grid) {
        const { colPositions, rowPositions } = computeGridPositions(grid);
        const anchor = resolveAuraAnchor(card, grid, colPositions.length - 1, rowPositions.length - 1, host._cellAtWorld);
        if (anchor) card._auraAnchor = anchor;
      }
    }
    moved = true;
  }
  if (moved) {
    host.recomputeStaticAuras?.();
    host.forceUpdate?.();
  }
}

// S4. Relocate ONE card's mesh + body to its authoritative server cell,
// ignoring the ownership/drag skip guards foldPositionsFromMirror applies. Used
// on a rejected/optimistic-illegal drop: the acting client returns the card to
// the exact cell-derived pose the opponent already renders (and that the
// cosmetic release relay then broadcasts), so no actor-vs-opponent divergence
// and no render-parity displacement. Returns true when it moved the card.
export function snapCardToServerCell(host, id, { glide = false } = {}) {
  const mirror = host._authMirror?.getState?.();
  const mc = mirror?.cards?.[id];
  if (!mc || mc.zone !== 'play') return false;
  const mesh = host.meshes.get(id);
  if (!mesh) return false;
  const card = mesh.userData?.cardInstance;
  // Restore the stack fields with the pose. The Y/renderOrder pass reads
  // them off this instance, and the release path is the last chance to fold
  // them when no further diff arrives (same stamp as foldPositionsFromMirror).
  if (card && mc.cell) card.cell = { col: mc.cell.col, row: mc.cell.row };
  if (card && mc.stackSeq !== undefined) card.stackSeq = mc.stackSeq;
  if (card && mc.subsurface !== undefined) card.subsurface = mc.subsurface;
  // Free card placement: the stored drop coords ARE the slot (see
  // foldPositionsFromMirror). "snap to server cell" means those coords.
  const freePlaced = (host._freeCardPlacement?.() || host._automationsEnabled?.() === false)
    && !card?.isSite && card?.type !== 'Site'
    && mc.pos && typeof mc.pos.x === 'number' && typeof mc.pos.z === 'number';
  const rpos = (card?.type === 'Aura' || freePlaced)
    ? null : restPosesFromMirror(host, mirror).get(id);
  const pos = rpos || (mc.pos && typeof mc.pos.x === 'number' ? mc.pos : null);
  if (!pos) return false;
  if (glide) {
    // Drop-in-place feel: ease the mesh to its layout slot instead of
    // teleporting (same glide the folds use).
    glideMeshXZ(host, mesh, pos.x, pos.z);
  } else {
    const body = mesh.userData?.body;
    if (body) {
      const q = body.quaternion;
      applyRemotePoseAndRest(body, [pos.x, mesh.position.y, pos.z], [q.x, q.y, q.z, q.w]);
      syncMeshFromBody(mesh);
    } else {
      mesh.position.x = pos.x;
      mesh.position.z = pos.z;
    }
  }
  if (card) { card.x = pos.x; card.z = pos.z; }
  return true;
}

// Observer-side divergence guard for cosmetic drags. A relayed drag whose
// move the server REJECTED (authz, unreachable) produces NO diff. The
// observing client would keep the relayed ghost pose forever while the
// actor's pending-move TTL sweep reverts. After the pending window, glide
// the card to its mirror slot; a confirmed move's diff has already folded
// it there, making this a no-op. Skipped when a new interaction owns the
// card by the time the timer fires.
export const RELEASE_RECONCILE_MS = 1800;

export function scheduleReleaseReconcile(host, cardId, delay = RELEASE_RECONCILE_MS) {
  setTimeout(() => {
    if (host._unmounted) return;
    if (!host.ownership?.is?.(cardId, 'free')) return;
    if (host.dragging?.draggingIds?.has?.(cardId)) return;
    if (host._combatSelect?.chosen?.has?.(cardId)) return;
    host._snapCardToServerCell?.(cardId, { glide: true });
  }, delay);
}

// Server-authoritative Collection share (R2d): the opponent toggled their eye,
// so their collection cards are now REVEALED in our projection (full identity in
// mirror.cards, owner=opp, zone='collection'). Build a synthetic opponent
// Collection pile in gameState.piles from the mirror so the existing collection
// tab UI (renderBody) renders it. And, because this rebuilds on every fold
// (incl. the reconnect Full), the share SURVIVES reconnect (unlike the legacy
// ephemeral pile:sync). Removes the pile when the share is withdrawn (no revealed
// cards), closing its view if open.
export function applyRevealedPilesFromMirror(host, mirrorState) {
  // Solo: no hidden opponent. The pilot already renders both seats' real
  // Collections; there is nothing to share/reveal.
  if (host.props?.isCasualSandbox) return;
  const gs = host.state?.gameState;
  if (!gs?.piles) return;

  const localIsHost = !!host.state?.isHost;
  const oppSeat = localIsHost ? 'p2' : 'p1';
  const oppRotated = localIsHost; // opponent spawns rotated when we are host

  // The TAB is driven by the share FLAG, not by card presence. A collection is
  // usually empty at game start, and sharing an empty one must still surface the
  // (empty) tab, exactly like the always-visible Banished tab.
  const shared = !!mirrorState?.revealedPiles?.[oppSeat]?.collection;
  const mirrorCards = mirrorState?.cards;
  const revealed = [];
  if (shared && mirrorCards) {
    for (const mc of Object.values(mirrorCards)) {
      if (mc && mc.owner === oppSeat && mc.zone === 'collection' && mc.cardId != null) revealed.push(mc);
    }
  }

  const idx = gs.piles.findIndex((p) => p.name === 'Collection' && !!p.rotated === oppRotated);
  const existing = idx === -1 ? null : gs.piles[idx];

  if (!shared) {
    if (existing) {
      const next = gs.piles.slice();
      next.splice(idx, 1);
      host.setState({ gameState: { ...gs, piles: next } });
      const viewing = host.state.searchPile;
      if (viewing && viewing.id === existing.id) host.handlePileSearchClose?.();
    }
    return;
  }

  // Idempotent: skip the rebuild when the revealed id set already matches.
  const ids = revealed.map((mc) => mc.instanceId).sort();
  if (existing && existing.cards.length === ids.length) {
    const have = existing.cards.map((c) => c.id).sort();
    if (have.every((id, i) => id === ids[i])) return;
  }

  // Resolve each revealed card's identity from the local catalog (the mirror
  // carries only the cardId; name/art come from sorceryCards).
  const byId = host._sorceryCardIndex
    || (host._sorceryCardIndex = new Map((host.props.sorceryCards || []).map((c) => [c.unique_id, c])));
  const cards = revealed
    .map((mc) => {
      const cat = byId.get(mc.cardId);
      return cat ? instanceFromMirror(cat, mc, oppRotated, mc.instanceId) : null;
    })
    .filter(Boolean);

  const next = gs.piles.slice();
  const built = existing
    ? { ...existing, cards, revealed: true }
    : { id: `collection-${oppSeat}`, name: k('Collection'), cards, x: 0, z: 0, rotated: oppRotated, revealed: true };
  if (idx === -1) next.push(built);
  else next[idx] = built;
  host.setState({ gameState: { ...gs, piles: next } });
}
